/**
 * @fileoverview Data Layer (DL) for the Farmeando Aura game dynamic.
 * Provides native fetch requests to communicate with the FarmeandoAura API controller endpoints.
 * 
 * @module utilities/DL/FarmeandoAura
 */

/**
 * Retrieves all registered themes for Farmeando Aura.
 * 
 * @async
 * @function GetThemes
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} API response payload.
 */
export const GetThemes = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/GetThemes`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al obtener la lista de temas.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al obtener temas.' };
    }
};

/**
 * Retrieves a single theme by its unique numeric identifier.
 * 
 * @async
 * @function GetTheme
 * @param {number} themeIdentifier - Unique theme ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const GetTheme = async (themeIdentifier) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/GetTheme/${themeIdentifier}`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al obtener el tema seleccionado.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al obtener el tema.' };
    }
};

/**
 * Creates or updates a Farmeando Aura theme.
 * 
 * @async
 * @function SaveTheme
 * @param {Object} themeData - Theme payload object.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const SaveTheme = async (themeData) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/SaveTheme`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(themeData)
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al guardar el tema.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al guardar el tema.' };
    }
};

/**
 * Deletes a theme by its unique numeric identifier.
 * 
 * @async
 * @function DeleteTheme
 * @param {number} themeIdentifier - Unique theme ID.
 * @returns {Promise<{success: boolean, message?: string}>} API response payload.
 */
export const DeleteTheme = async (themeIdentifier) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/DeleteTheme/${themeIdentifier}`, {
            method: 'DELETE'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al eliminar el tema.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al eliminar el tema.' };
    }
};

/**
 * Clones an existing theme by ID into a new theme record.
 * 
 * @async
 * @function CloneTheme
 * @param {number} themeIdentifier - Source theme ID to clone.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const CloneTheme = async (themeIdentifier) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/CloneTheme/${themeIdentifier}`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al clonar el tema.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al clonar el tema.' };
    }
};

/**
 * Retrieves the global game room configuration for Farmeando Aura.
 * 
 * @async
 * @function GetConfig
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const GetConfig = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/GetConfig`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al obtener la configuración.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al obtener configuración.' };
    }
};

/**
 * Saves the global game room configuration for Farmeando Aura.
 * 
 * @async
 * @function SaveConfig
 * @param {Object} configurationData - Room configuration payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const SaveConfig = async (configurationData) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/SaveConfig`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(configurationData)
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al guardar la configuración.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al guardar configuración.' };
    }
};

/**
 * Retrieves the historical log of completed Farmeando Aura matches.
 * 
 * @async
 * @function GetMatchesHistory
 * @param {number} [queryLimit=50] - Maximum number of matches to retrieve.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} API response payload.
 */
export const GetMatchesHistory = async (queryLimit = 50) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/GetMatchesHistory?limit=${queryLimit}`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al obtener el historial de partidas.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al obtener historial.' };
    }
};

/**
 * Uploads a single media file to be compressed and saved on the server.
 * 
 * @async
 * @function UploadMedia
 * @param {FormData} multipartFormData - Multipart form data containing 'file' and optional 'type'.
 * @returns {Promise<{success: boolean, url?: string, message?: string}>} API response payload.
 */
export const UploadMedia = async (multipartFormData) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/UploadMedia`, {
            method: 'POST',
            body: multipartFormData
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al subir el archivo multimedia.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al subir archivo multimedia.' };
    }
};

/**
 * Uploads a batch of media files to be compressed and saved simultaneously on the server.
 * 
 * @async
 * @function UploadBatchMedia
 * @param {FormData} multipartFormData - Multipart form data containing multiple 'files' and optional 'type'.
 * @returns {Promise<{success: boolean, urls?: Array<string>, count?: number, message?: string}>} API response payload.
 */
export const UploadBatchMedia = async (multipartFormData) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/UploadBatchMedia`, {
            method: 'POST',
            body: multipartFormData
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al subir lote de archivos multimedia.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al subir lote multimedia.' };
    }
};

/**
 * Emits a real-time command to project the clean promotional cover screen.
 * 
 * @async
 * @function ShowCover
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} API response payload.
 */
export const ShowCover = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/ShowCover`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al mostrar la portada.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al mostrar la portada.' };
    }
};

/**
 * Emits a real-time command to project the interactive combat HUD screen.
 * 
 * @async
 * @function ShowGame
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} API response payload.
 */
export const ShowGame = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/ShowGame`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al proyectar el juego.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al proyectar el juego.' };
    }
};

/**
 * Starts a new combat round with countdown and timestamp synchronization.
 * 
 * @async
 * @function StartGame
 * @param {Object} [gameOptions={ durationSeconds: 10, gameMode: 'simultaneous' }] - Round startup parameters.
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} API response payload.
 */
export const StartGame = async (gameOptions = { durationSeconds: 10, gameMode: 'simultaneous' }) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/StartGame`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(gameOptions)
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al iniciar la partida.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al iniciar la partida.' };
    }
};

/**
 * Pauses the active combat round in real time.
 * 
 * @async
 * @function PauseGame
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} API response payload.
 */
export const PauseGame = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/PauseGame`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al pausar la partida.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al pausar la partida.' };
    }
};

/**
 * Resumes a paused combat round in real time.
 * 
 * @async
 * @function ResumeGame
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} API response payload.
 */
export const ResumeGame = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/ResumeGame`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al reanudar la partida.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al reanudar la partida.' };
    }
};

/**
 * Triggers an emergency stop, ending the round early and calculating immediate scores.
 * 
 * @async
 * @function FinishEarly
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} API response payload.
 */
export const FinishEarly = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/FinishEarly`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al finalizar anticipadamente.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al finalizar anticipadamente.' };
    }
};

/**
 * Resets the room to idle status, clearing active scores and timers.
 * 
 * @async
 * @function ResetGame
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} API response payload.
 */
export const ResetGame = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/ResetGame`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al reiniciar la partida.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al reiniciar la partida.' };
    }
};

/**
 * Retrieves a real-time snapshot of the active game state from the backend engine.
 * 
 * @async
 * @function GetRealtimeState
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const GetRealtimeState = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FarmeandoAura/GetRealtimeState`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al obtener el estado en tiempo real.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al consultar el estado.' };
    }
};
